import React from "react";
import {Avatar, AvatarGroup, Box, ButtonBase, Stack, Tooltip, Typography} from "@mui/material";
import {Link} from "react-router-dom";
import {HugeiconsIcon} from "@hugeicons/react";
import {Building03Icon, HeadsetIcon, Robot01Icon, UsersRoundIcon} from "@hugeicons/core-free-icons";
import {ProjectStageProgress} from "./project-stage-status";
import {ProjectRowMenu} from "./project-row-menu";
import {ProjectActiveCallsChart} from "./project-active-calls-chart";
import {AVATAR_COLORS} from "../../registry-project/dashboard/components/conversationUi";
import {subProjectLabel} from "../../../utils/subProjects";

const TYPE_LABELS = {
  VOICE_ASSISTANT: "Voice assistant",
  CHATBOT: "Chatbot",
};

// Columns of a project row, shared with the column titles of the list (desktop)
export const PROJECT_GRID_COLUMNS = "minmax(0, 2.4fr) minmax(110px, 0.8fr) 210px minmax(150px, 1fr) 40px";

// Registry project page for one sub project; the project name opens its first sub project
const getSubProjectUrl = (project, subProject) => project?.id && subProject?.id != null
  ? `/registry-project/${project.id}/${subProject.id}`
  : null;

const chipSx = {
  display: "inline-flex", alignItems: "center", gap: 0.625, height: 24, px: 1, borderRadius: "6px",
  fontSize: 12, fontWeight: 500, color: "#4A4A63", bgcolor: "#F2F2F7", whiteSpace: "nowrap", textDecoration: "none",
  transition: "background-color .15s ease, color .15s ease",
};

const LiveDot = ({live}) => (
  <Box component={"span"} sx={{width: 6, height: 6, borderRadius: "50%", flexShrink: 0, bgcolor: live ? "#22C55E" : "#C9CAD8"}}/>
);

// A sub project chip that opens it
const SubProjectChip = ({project, subProject}) => {
  const url = getSubProjectUrl(project, subProject);
  return (
    <ButtonBase
      component={url ? Link : "span"}
      to={url || undefined}
      sx={{...chipSx, ...(url && {"&:hover": {bgcolor: "rgba(132, 145, 247, 0.14)", color: "#4B55C9"}})}}
    >
      <LiveDot live={subProject.live}/>
      {subProjectLabel(subProject)}
    </ButtonBase>
  );
};

// One sub project is shown as its chip; several as "{count} sub projects", listed (and openable) on hover
const SubProjectChips = ({project, subProjects}) => {
  if (!subProjects.length) return null;
  if (subProjects.length === 1) {
    const subProject = subProjects[0];
    return (
      <Tooltip title={subProject.live ? "Live" : "Not live"}>
        <span><SubProjectChip project={project} subProject={subProject}/></span>
      </Tooltip>
    );
  }
  const liveCount = subProjects.filter(item => item.live).length;
  return (
    <Tooltip
      title={
        <Stack gap={0.5} sx={{py: 0.5}}>
          {subProjects.map(subProject => <SubProjectChip key={subProject.id} project={project} subProject={subProject}/>)}
        </Stack>
      }
      componentsProps={{tooltip: {sx: {bgcolor: "#fff", border: "1px solid #E4E4EE", boxShadow: "0 8px 24px rgba(35, 38, 77, 0.12)", p: 0.75}}}}
    >
      <Box component={"span"} tabIndex={0} sx={{...chipSx, cursor: "default"}}>
        <LiveDot live={liveCount > 0}/>
        {subProjects.length} sub projects
      </Box>
    </Tooltip>
  );
};

// Clicking the organization filters the project list by it
const OrganizationChip = ({project, onFilterOrganization}) => {
  if (!project?.organizationName) return null;
  return (
    <Tooltip title={`Show all ${project.organizationName} projects`}>
      <ButtonBase
        onClick={() => onFilterOrganization?.(project.organizationId, project.organizationName)}
        sx={{...chipSx, bgcolor: "#fff", border: "1px solid #E4E4EE", "&:hover": {bgcolor: "#F4F4FA", color: "#23264D"}}}
      >
        <HugeiconsIcon icon={Building03Icon} size={13}/>
        {project.organizationName}
      </ButtonBase>
    </Tooltip>
  );
};

// members: the project's members as the list sends them, masked (e.g. "john.doe@***", "agen**"); masked values can
// repeat, so they are keyed by position
const Members = ({members}) => {
  if (!members.length) return <Typography sx={{fontSize: 12, color: "#B4B4C3"}}>—</Typography>;
  const title = <Stack sx={{py: 0.25}}>{members.map((member, index) => <Typography key={index} sx={{fontSize: 12}}>{member}</Typography>)}</Stack>;
  return (
    <>
      <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={4000}>
        <Stack direction={"row"} alignItems={"center"} gap={0.75}
               sx={{display: {xs: "flex", md: "none"}, height: 28, px: 1.25, borderRadius: "999px", bgcolor: "#F2F2F7", color: "#6B6B80"}}>
          <HugeiconsIcon icon={UsersRoundIcon} size={15}/>
          <Typography sx={{fontSize: 13, fontWeight: 600, lineHeight: 1, color: "inherit"}}>{members.length}</Typography>
        </Stack>
      </Tooltip>
    <Tooltip title={title} enterTouchDelay={0} leaveTouchDelay={4000}>
      <AvatarGroup max={4} sx={{
        display: {xs: "none", md: "flex"},
        justifyContent: "flex-end", width: "fit-content",
        "& .MuiAvatar-root": {width: 30, height: 30, fontSize: 11, fontWeight: 700, border: "2px solid #fff"},
        "& .MuiAvatarGroup-avatar": {color: "#6B6B80", bgcolor: "#F2F2F7"},
      }}>
        {members.map((member, index) => {
          const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
          return (
            <Avatar key={index} alt={member} sx={{bgcolor: color.bg, color: color.color}}>
              {member?.slice(0, 2)?.toUpperCase()}
            </Avatar>
          );
        })}
      </AvatarGroup>
    </Tooltip>
    </>
  );
};

// One project of the list: name, organization and languages, members, active calls, stage and actions.
// A grid row on desktop (PROJECT_GRID_COLUMNS), stacked on small screens.
const ProjectRichTableRow = ({project, canManage, onFilterOrganization}) => {
  const subProjects = project?.subProjects || [];
  const mainSubProject = subProjects[0];
  const projectUrl = getSubProjectUrl(project, mainSubProject);
  const isChatbot = project?.type === "CHATBOT";
  const avatar = AVATAR_COLORS[(Number(project?.id) || 0) % AVATAR_COLORS.length];

  const members = (project?.members || []).filter(Boolean);

  const menu = canManage && mainSubProject?.flowUid
    ? <ProjectRowMenu flowUid={mainSubProject.flowUid} projectId={project?.id} subProjectId={mainSubProject.id}/>
    : null;

  const hasChips = subProjects.length > 0 || Boolean(project?.organizationName);
  const chips = (
    <>
      <OrganizationChip project={project} onFilterOrganization={onFilterOrganization}/>
      <SubProjectChips project={project} subProjects={subProjects}/>
    </>
  );

  return (
      <Box
          sx={{
              display: "grid",
              gridTemplateColumns: {
                  xs: "minmax(0, 1fr) auto",
                  md: PROJECT_GRID_COLUMNS,
              },
              gridTemplateAreas: {
                  xs: `"project menu" "chips chips" "stage members"`,
                  md: `"project members calls stage menu"`,
              },
              alignItems: "center",
              columnGap: 2,
              rowGap: 1.5,
              px: { xs: 2, md: 0 },
              py: { xs: 2, md: 1.5 },
              bgcolor: { xs: "#FAFAFD", md: "transparent" },
              mb: { xs: 1.5, md: 0 },
              borderRadius: { xs: "14px", md: 0 },
              transition: "background-color .15s ease",
              "&:hover": {
                  bgcolor: {
                      xs: "rgba(132, 145, 247, 0.08)",
                      md: "transparent",
                  },
              },
              "&:last-of-type": {
                  mb: 0,
              },
          }}
      >
      <Stack direction={"row"} gap={1.5} sx={{gridArea: "project", minWidth: 0}}>
        <Tooltip title={TYPE_LABELS[project?.type] || ""}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center",
            justifyContent: "center", bgcolor: avatar.bg, color: avatar.color,
          }}>
            <HugeiconsIcon icon={isChatbot ? Robot01Icon : HeadsetIcon} size={20}/>
          </Box>
        </Tooltip>
        <Box sx={{minWidth: 0}}>
          <Typography
            component={projectUrl ? Link : "span"}
            to={projectUrl || undefined}
            sx={{
              display: "block", fontSize: 16, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em",
              textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis",
              whiteSpace: {xs: "normal", md: "nowrap"}, overflowWrap: {xs: "anywhere", md: "normal"},
              ...(projectUrl && {"&:hover": {color: "#4B55C9"}}),
            }}
          >
            {project?.name || "Untitled project"}
          </Typography>
          <Typography noWrap sx={{display: {xs: "none", md: "block"}, fontSize: 13, color: "#8A8AA0", minHeight: 4}}>
            {project?.description || TYPE_LABELS[project?.type] || ""}
          </Typography>
          {hasChips && (
            <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} sx={{mt: 1, display: {xs: "none", md: "flex"}}}>
              {chips}
            </Stack>
          )}
        </Box>
      </Stack>

      {hasChips && (
        <Stack direction={"row"} gap={0.75} flexWrap={"wrap"} sx={{gridArea: "chips", display: {xs: "flex", md: "none"}}}>
          {chips}
        </Stack>
      )}

      <Box sx={{gridArea: "members", justifySelf: {xs: "end", md: "start"}}}>
        <Members members={members}/>
      </Box>

      <Box sx={{gridArea: "calls", minWidth: 0, display: {xs: "none", md: "block"}}}>
        {isChatbot
          // active-call snapshots are recorded for voice calls only
          ? <Typography sx={{display: {xs: "none", md: "block"}, fontSize: 12, color: "#B4B4C3", textAlign: "center"}}>—</Typography>
          : <ProjectActiveCallsChart project={project}/>}
      </Box>

      <Box sx={{gridArea: "stage"}}>
        <ProjectStageProgress subProjects={subProjects}/>
      </Box>

      <Box sx={{gridArea: "menu", justifySelf: "end", alignSelf: {xs: "start", md: "center"}}}>
        {menu}
      </Box>
    </Box>
  );
};

export default ProjectRichTableRow;
